import sortBy from "lodash/sortBy";
import {
  FiAlertTriangle,
  FiBell,
  FiCalendar,
  FiCheckCircle,
  FiMapPin,
  FiMessageSquare,
  FiSettings,
} from "react-icons/fi";
import {HiOutlineTemplate} from "react-icons/hi";
import {LuChartColumnIncreasing, LuFileChartPie} from "react-icons/lu";
import {PiFlagBold} from "react-icons/pi";
import {RiLightbulbLine} from "react-icons/ri";

import {useCurrentUserContext, useZendeskMessenger} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {NavBarAccordionItem} from "@/components/navBar/subComponents/NavBarAccordion/NavBarAccordionItem.tsx";
import {NavBarItem} from "@/components/navBar/subComponents/NavBarItem/NavBarItem.tsx";
import {NavBarNavItemsAccordionWrapper} from "@/components/navBar/subComponents/NavBarNavItems/NavBarNavItemsAccordionWrapper.tsx";
import {NavBarWhatsNew} from "@/components/navBar/subComponents/NavBarWhatsNew/NavBarWhatsNew.tsx";
import {MAINTENANCE_SCHEDULES} from "@/lib/featureFlags.ts";

export const NavBarNavItems = () => {
  const {user, brand, pinnedDashboards, userHasFeature} = useCurrentUserContext();
  const {open: openMessenger} = useZendeskMessenger();

  // technically an admin user can come from a subaccount, practically DB records have to be reviewed
  const isSubaccount = user?.account.is_subaccount;
  return (
    <NavBarNavItemsAccordionWrapper className="d-flex flex-column flex-grow-1 px-3 gap-2">
      <NavBarWhatsNew />
      {pinnedDashboards.length == 0 ? (
        <NavBarItem name="Dashboard" to="/dashboard/create" icon={HiOutlineTemplate} />
      ) : (
        <NavBarAccordionItem name={"Dashboard"} icon={HiOutlineTemplate}>
          {sortBy(pinnedDashboards, (d) => d.ordering).map((dashboard) => (
            <NavBarItem key={dashboard.id} name={dashboard.name} to={`/dashboard/${dashboard.id}`} />
          ))}
        </NavBarAccordionItem>
      )}
      <NavBarItem
        name="Monitoring"
        to="/checks"
        icon={LuChartColumnIncreasing}
        specialCasePages={["^/assistant/checks/create"]}
      />
      {/* Hidden until the Assistant grows beyond bulk check creation.
          To enable: set visible and remove specialCasePages from Monitoring above. */}
      <NavBarItem name="Assistant" to="/assistant" icon={FiCheckCircle} visible={false} />
      <NavBarAccordionItem
        name={"Status Pages"}
        icon={PiFlagBold}
        specialCasePages={["/incidents/create", "^/incidents/[^/]+/edit$"]}
      >
        <NavBarItem name="All Status Pages" to="/statuspages" />
        <NavBarItem name="Incidents" to="/incidents" />
      </NavBarAccordionItem>
      <NavBarItem name="Alerts" to="/alerts" icon={FiAlertTriangle} />
      {userHasFeature(MAINTENANCE_SCHEDULES) && <NavBarItem name="Maintenance" to="/maintenance" icon={FiCalendar} />}
      <NavBarAccordionItem name="Reports" icon={LuFileChartPie}>
        <NavBarItem name="SLA Reports" to="/reports/sla" />
        <NavBarItem name="Scheduled Reports" to="/reports/scheduled" />
        <NavBarItem name="Real User Monitoring" to="/rum/v2" />
      </NavBarAccordionItem>
      <Divider color={"nav"} />
      <NavBarAccordionItem name={"Notification"} icon={FiBell}>
        <NavBarItem name="Contacts" to="/contact-groups" />
        <NavBarItem name="Integrations" to="/integrations/manage/" />
        <NavBarItem name="Mobile Apps" to="/mobile-apps" visible={brand.isDefault} />
        <NavBarItem name="Uptime Widget" to="/widget" visible={brand.isDefault} />
      </NavBarAccordionItem>
      <NavBarItem name="Probe Servers" to="/monitoring" icon={FiMapPin} />
      <NavBarAccordionItem name={"Settings"} icon={FiSettings}>
        <NavBarItem
          name="User Management"
          to="/accounts/user-management"
          visible={user?.has_admin_access && !isSubaccount}
        />
        <NavBarItem name="Billing & Subscriptions" to="/billing" />
        <NavBarItem name="SSO" to="/accounts/single-sign-on" visible={user?.has_admin_access && brand.isDefault} />
        <NavBarItem name="API" to="/api/tokens" />
        <NavBarItem name="Audit Log" to="/audit/log" visible={user?.account.enable_audit_log} />
        <NavBarItem name="Tags" to="/tags" />
        <NavBarItem name="Secure Vault" to="/vault" />
      </NavBarAccordionItem>

      <NavBarItem
        name="Suggest a Feature"
        icon={RiLightbulbLine}
        to="https://feedback.uptime.com"
        target={"_blank"}
        iconClassName={"pulse-color-yellow-500"}
      />
      <NavBarItem as="button" type="button" name="Contact Support" icon={FiMessageSquare} onClick={openMessenger} />
    </NavBarNavItemsAccordionWrapper>
  );
};
